import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Translate, withLocalize } from "react-localize-redux";
import { useOutletContext, useParams } from "react-router";
import Navigation from "../../../utils/components/Navigation";
import { BsEyeFill, BsPlusCircle } from "react-icons/bs";
import { AiOutlineSearch } from "react-icons/ai";
import DataTable from "../../../utils/components/DataTable";
import getDefaultAvatar from "../../../utils/helpers/getDefaultAvatar";
import { RiPencilFill } from "react-icons/ri";
import { MdLockOpen } from "react-icons/md";
import { FiRepeat } from "react-icons/fi";
import NewCompanyUserModal from "./components/NewCompanyUserModal";
import UserViewModal from "./components/UserViewModal";
import DelegateTasksModal from "./components/DelegateTasksModal";
import DeactivateWarningModal, { DEACTIVATE_VARIANT } from "./components/DeactivateWarningModal";
import Loading from "../../../utils/components/Loading";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import toastr from "toastr";
import { useCan } from "../../../utils/hooks/useCan";
import { useUser } from "../../../utils/hooks/useUser";
import {
  isProjectAdmin,
  isReducedUserRow,
  LEVELED_ROLE_NAMES,
} from "../../../utils/roles";

const USER_STATUSES = ["active", "passive"];

const SEARCH_TIMEOUT = 800;

const CompanyUsers = ({ translate }) => {
  const {
    setBreadcrumbs,
    users,
    groups,
    company,
    totalUsers,
    usersLoading,
    getUsers,
  } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [userViewInfo, setUserViewInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [isModalOn, setIsModalOn] = useState(false);
  const [isUserViewModalOn, setIsUserViewModalOn] = useState(false);
  const [delegateSourceUser, setDelegateSourceUser] = useState(null);
  // Deactivation warning ({user, count, variant}) and the user whose tasks are delegated before deactivating
  const [deactivation, setDeactivation] = useState(null);
  const [deactivationDelegate, setDeactivationDelegate] = useState(null);
  const [openTaskCounts, setOpenTaskCounts] = useState({});
  const openTaskCountsRequest = useRef(0);

  const { id } = useParams();
  const { project } = useProject();

  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [searchedQuery, setSearchedQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const [userRoles, setUserRoles] = useState([]);

  const [sortQuery, setSortQuery] = useState({
    sortBy: "user_name",
    orderBy: "asc",
  });

  const can = useCan();
  const authUser = useUser();
  const authIsProjectAdmin = isProjectAdmin(authUser);
  const isLockedForProjectAdmin = (row) =>
    authIsProjectAdmin &&
    String(row.id) !== String(authUser?.id) &&
    LEVELED_ROLE_NAMES.includes(row.application_role?.name);

  const canDelegate =
    Boolean(project?.id) &&
    can.doThat("user:update", {
      company_id: id,
      project_id: company?.projects?.find((p) => p?.id === project?.id)?.id,
    });

  const loadOpenTaskCounts = () => {
    const requestId = ++openTaskCountsRequest.current;
    const userIds = (users || []).map((user) => user.id);
    if (!canDelegate || !userIds.length) {
      setOpenTaskCounts({});
      return;
    }
    API.postData("tasks/open_counts", { project_id: project.id, user_ids: userIds })
      .then(({ data: res }) => {
        // Ignore a response that arrives after a newer page was requested
        if (requestId !== openTaskCountsRequest.current) return;
        setOpenTaskCounts(Object.fromEntries((res.data || []).map((row) => [row.user_id, row.total])));
      })
      .catch(() => {
        if (requestId === openTaskCountsRequest.current) setOpenTaskCounts({});
      });
  };

  // Keyed on ids, not the array: the list is fetched more than once for the same page
  const pageUserIds = (users || []).map((user) => user.id).join(",");

  useEffect(() => {
    loadOpenTaskCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageUserIds, project?.id, canDelegate]);

  // Drop a counts response that arrives after leaving the page (e.g. via the Form Box link)
  useEffect(() => () => {
    openTaskCountsRequest.current += 1;
  }, []);

  const columns = [
    {
      name: "#",
      selector: (row) => row.avatar,
      center: true,
      compact: true,
      maxWidth: "90px",
      cell: (row) =>
        row.avatar ? (
          <div className="avatar rounded-circle d-flex justify-content-center align-items-center overflow-hidden">
            <img
              src={row.avatar}
              alt={row.name}
              className="w-100 h-100 rounded-circle"
            />
          </div>
        ) : (
          <div className="default-avatar">{getDefaultAvatar(row.name)}</div>
        ),
    },
    {
      name: <Translate id="company.users.headers.user-name"/>,
      selector: (row) => row.name,
      sortable: true,
      left: true,
      sortField: "user_name",
    },
    {
      name: <Translate id="company.users.headers.user-role"/>,
      selector: (row) => row.application_role?.display_name,
      sortable: true,
      center: true,
      cell: (row) =>
        isReducedUserRow(row) ? (
          "-"
        ) : (
          <div className={`role role--${row.application_role.id}`}>
            {row.application_role.display_name}
          </div>
        ),
      sortField: "application_role",
    },
    {
      name: <Translate id="company.users.headers.user-status"/>,
      selector: (row) => row.status,
      center: true,
      cell: (row) =>
        isReducedUserRow(row) ? (
          "-"
        ) : (
          <div className={`status status--${row.status}`}>
            {row.status
              ? USER_STATUSES[0].toLocaleUpperCase()
              : USER_STATUSES[1].toLocaleUpperCase()}
          </div>
        ),
    },
    {
      name: <Translate id="company.users.headers.operations"/>,
      center: true,
      cell: (row) => (
        <div className="actions-holder d-flex align-items-center justify-content-between">
          <div
            role="button"
            className="action-item"
            onClick={() => {
              setSelectedUserId(row.id);
              handleClickUserView(row);
            }}
          >
            <BsEyeFill/>
          </div>
          {can.doThat("user:update", {
            company_id: id,
            project_id: company?.projects?.find((p) => p?.id === project?.id)
              ?.id,
          }) &&
            !isLockedForProjectAdmin(row) && (
            <div
              role="button"
              title={
                isReducedUserRow(row)
                  ? translate("user.add_to_project")
                  : undefined
              }
              onClick={() => {
                setSelectedUserId(row.id);
                setIsModalOn(true);
              }}
              className="action-item"
            >
              {isReducedUserRow(row) ? <BsPlusCircle/> : <RiPencilFill/>}
            </div>
          )}
          {!authIsProjectAdmin && can.doThat("user:update", {
            company_id: id,
            project_id: company?.projects?.find((p) => p?.id === project?.id)
              ?.id,
          }) && (
            <div
              role="button"
              className="action-item"
              onClick={() => handleActivityStatus(row)}
            >
              <MdLockOpen/>
            </div>
          )}
          {canDelegate &&
            (openTaskCounts[row.id] === 0 ? (
              <div
                role="button"
                className="action-item delegate-action disabled"
                aria-disabled="true"
                title={translate("company.users.delegate.no_open_tasks_tooltip")}
                aria-label={translate("company.users.delegate.no_open_tasks_tooltip")}
              >
                <FiRepeat/>
              </div>
            ) : (
              <div
                role="button"
                className="action-item delegate-action"
                title={translate("company.users.delegate.action_tooltip")}
                aria-label={translate("company.users.delegate.action_tooltip")}
                onClick={() => setDelegateSourceUser({ id: row.id, name: row.name })}
              >
                <FiRepeat/>
              </div>
            ))}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setTotalRows(totalUsers);
  }, [totalUsers]);

  const getUserById = (id) => {
    let url = "user/" + id;
    return API.getData(url).then(({ data: res }) => {
      return res.data;
    });
  };

  const updateUserStatus = (user, status, successMessage) => {
    setIsLoading(true);

    return API.updateUserData({ status }, user.id)
      .then(() => {
        toastr.success(successMessage || translate("company.users.messages.user-status-update-success"));
        handleGetCompanyUsers();
        loadOpenTaskCounts();
      })
      .catch(() => setIsLoading(false));
  };

  const getOpenTaskSummary = (userId) =>
    API.getData(`user/open_task_summary/${userId}`).then(({ data: res }) => res.data);

  // The API interceptor already shows 4xx errors; network failures reach here as raw axios errors
  const showSummaryError = (error) => {
    if (error?.isAxiosError) toastr.error(translate("company.users.delegate.errors.request_failed"));
  };

  // Only the selected project is shown and delegated here; tasks in other projects still block on the server
  const decideDeactivation = (user, summary, successMessage) => {
    if (summary.total === 0) {
      updateUserStatus(user, false, successMessage);
      return;
    }

    const selected = summary.projects.find((row) => row.project_id === project?.id);
    if (!selected) {
      setDeactivation({ user, count: 0, variant: DEACTIVATE_VARIANT.otherProjects });
      return;
    }

    const variant = selected.can_delegate ? DEACTIVATE_VARIANT.delegate : DEACTIVATE_VARIANT.noPermission;
    setDeactivation({ user, count: selected.total, variant });
  };

  // Active users with open tasks are not deactivated; the warning offers to delegate them first
  const handleActivityStatus = (row) => {
    if (!row.status) {
      updateUserStatus(row, true);
      return;
    }

    const user = { id: row.id, name: row.name };
    getOpenTaskSummary(row.id)
      .then((summary) => decideDeactivation(user, summary))
      .catch(showSummaryError);
  };

  const startDeactivationDelegate = () => {
    setDeactivationDelegate(deactivation.user);
    setDeactivation(null);
  };

  // After delegating, the counts are read again: deactivate, report what is left here, or point to other projects
  const handleDeactivationDelegateClose = () => {
    const user = deactivationDelegate;
    setDeactivationDelegate(null);
    loadOpenTaskCounts();

    getOpenTaskSummary(user.id)
      .then((summary) => {
        const selected = summary.projects.find((row) => row.project_id === project?.id);
        if (summary.total > 0 && selected) {
          toastr.info(translate("company.users.deactivate.still_active", { name: user.name, count: selected.total }));
          return;
        }
        decideDeactivation(user, summary, translate("company.users.deactivate.deactivated", { name: user.name }));
      })
      .catch(showSummaryError);
  };

  const handleClickUserView = async (row) => {
    const currentUserData = await getUserById(row.id);

    setUserViewInfo({
      Name: currentUserData.name,
      Title: currentUserData.title,
      Email: currentUserData.email,
      Company: company.name,
    });

    setIsUserViewModalOn(true);
  };

  useEffect(() => {
    if (!users) return;
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  useEffect(() => {
    setBreadcrumbs([
      {
        id: 0,
        title: "page_titles.companies",
        route: "auth.companies",
      },
      { id: 1, customTitle: company?.name || "" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (!isModalOn) {
      setSelectedUserId(null);
    }
  }, [isModalOn]);

  const handleSearch = (e) => {
    const query = e.target.value;

    setIsLoading(true);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (query === searchedQuery || !query) {
      setIsLoading(false);
      setQuery(query);
      return;
    }

    setSearchTimeout(
      setTimeout(() => {
        setPage(1);
        setQuery(query);
      }, SEARCH_TIMEOUT),
    );
  };

  const handleGetCompanyUsers = (
    sortBy = sortQuery?.sortBy,
    orderBy = sortQuery?.orderBy,
  ) => {
    if (sortBy && orderBy) {
      setSortQuery({ sortBy, orderBy });
    }

    getUsers({
      sortBy,
      orderBy,
      page,
      rowsPerPage,
      query,
    });

    setSearchedQuery(query);
  };

  useEffect(() => {
    handleGetCompanyUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, page, rowsPerPage]);

  const handleSort = (column, orderBy) => {
    const { sortField: sortBy } = column;

    if (!sortBy || !orderBy) return;

    setSortQuery({ sortBy, orderBy: orderBy });
    getUsers({ sortBy, orderBy, page, rowsPerPage, query });
  };

  return (
    <div className="CompanyDetailPage CompanyUsers">
      <Helmet>
        <title>{translate("page_titles.company-users")}</title>
      </Helmet>
      <header>
        <Navigation
          title={translate("company.users.title")}
          inputPlaceholder={translate("company.filter_placeholder")}
          inputOnChange={handleSearch}
          iconNode={<AiOutlineSearch className="fs-5 me-2 mt-2"/>}
          buttonNode={
            project?.id &&
            can.doThat("user:create", {
              company_id: id,
              project_id: company?.projects?.find((p) => p?.id === project?.id)
                ?.id,
            }) ? (
              <p className="m-0 p-0 text-center">
                <BsPlusCircle className="fs-4 me-2"/>
                {translate("company.users.new_user")}
              </p>
            ) : (
              false
            )
          }
          onButtonClick={() => {
            setSelectedUserId(null);
            setIsModalOn(true);
          }}
        />
      </header>

      <DataTable
        pagination
        data={users}
        columns={columns}
        paginationServer
        paginationTotalRows={totalRows}
        paginationPerPage={rowsPerPage}
        onChangeRowsPerPage={(rowsPerPage) => {
          setRowsPerPage(rowsPerPage);
          setResetPaginationToggle(!resetPaginationToggle);
        }}
        onChangePage={(page) => {
          setPage(page);
          setResetPaginationToggle(!resetPaginationToggle);
        }}
        progressPending={isLoading || usersLoading}
        progressComponent={
          <div className="py-3">
            <Loading classNames={"my-3"}/>
          </div>
        }
        noDataComponent={
          <div className="text-center py-3">
            {translate("company.users.no_users")}
          </div>
        }
        onSort={handleSort}
        sortServer
        // paginationResetDefaultPage={resetPaginationToggle}
      />

      {can.doThat("user:create", {
        company_id: id,
        project_id: company?.projects?.find((p) => p?.id === project?.id)?.id,
      }) && (
        <NewCompanyUserModal
          onClose={() => {
            setIsModalOn(false);
          }}
          selectedUserId={selectedUserId}
          companyId={id}
          projectId={project?.id}
          groups={groups}
          show={isModalOn}
          refreshData={handleGetCompanyUsers}
          translate={translate}
          userRoles={userRoles}
          setUserRoles={setUserRoles}
          companyProjects={company?.projects}
          page={page}
        />
      )}
      {delegateSourceUser && project?.id && (
        <DelegateTasksModal
          show={Boolean(delegateSourceUser)}
          sourceUser={delegateSourceUser}
          projectId={project.id}
          translate={translate}
          onClose={() => {
            setDelegateSourceUser(null);
            loadOpenTaskCounts();
          }}
        />
      )}
      {deactivation && (
        <DeactivateWarningModal
          show={Boolean(deactivation)}
          user={deactivation.user}
          count={deactivation.count}
          variant={deactivation.variant}
          translate={translate}
          onCancel={() => setDeactivation(null)}
          onContinue={startDeactivationDelegate}
        />
      )}
      {deactivationDelegate && project?.id && (
        <DelegateTasksModal
          show={Boolean(deactivationDelegate)}
          sourceUser={deactivationDelegate}
          projectId={project.id}
          translate={translate}
          onClose={handleDeactivationDelegateClose}
        />
      )}
      <UserViewModal
        show={isUserViewModalOn}
        hideModal={() => setIsUserViewModalOn(false)}
        userInfo={userViewInfo}
      />
    </div>
  );
};

export default withLocalize(CompanyUsers);
